// =============================================================================
// totpEnrolement.js — Préparation de l'enrôlement 2FA (TOTP), robuste au mobile
// =============================================================================
// Incident du 24/09/2026 (Argentine Paoli, PWA Android) : chaque code était
// refusé (« incorrect ou expiré ») alors que l'heure du téléphone était juste.
// Cause : le secret connu d'Aramis n'était pas celui de l'application
// d'authentification. L'écran d'activation demandait de « pointer le téléphone
// vers le QR code » — impossible sur son propre écran — et il fallait recopier
// une clé de 32 caractères dans une autre application. En passant d'une
// application à l'autre, Android recharge souvent la PWA : un NOUVEAU secret
// était alors tiré, l'application gardait l'ancien (entrées « Aramis Enio »
// périmées), et le code ne pouvait plus jamais correspondre.
//
// Deux parades :
//   • la clé en attente est MÉMORISÉE sur l'appareil (24 h, jusqu'à l'activation) :
//     un rechargement reprend la même clé au lieu d'en tirer une autre ;
//   • sur téléphone, un lien otpauth:// ouvre directement l'application
//     d'authentification (Google / Microsoft Authenticator, Mots de passe
//     d'iOS), et la clé se copie d'un geste.
// La clé n'est active qu'après validation d'un code par aramisSetupTOTP ; elle
// est oubliée dès l'activation réussie.
// =============================================================================

const PREFIXE = "aramis_totp_attente_";
const DUREE_MS = 24 * 60 * 60 * 1000;
const RE_BASE32 = /^[A-Z2-7]{16,64}$/;

function lire(delegateId) {
  try {
    const brut = localStorage.getItem(PREFIXE + delegateId);
    if (!brut) return null;
    const v = JSON.parse(brut);
    if (!v || !RE_BASE32.test(String(v.secret || "")) || !(v.exp > Date.now())) return null;
    return v.secret;
  } catch { return null; }
}

/** Oublie la clé en attente (après activation réussie). */
export function oublierSecretEnAttente(delegateId) {
  try { localStorage.removeItem(PREFIXE + delegateId); } catch { /* stockage indisponible */ }
}

/**
 * Prépare l'enrôlement : reprend la clé en attente de ce compte si elle existe,
 * sinon en tire une nouvelle et la mémorise. Rend { secret (base32), uri }.
 */
export async function preparerEnrolement(delegateId, email) {
  const { TOTP, Secret } = await import("otpauth");
  const existante = delegateId ? lire(delegateId) : null;
  const secret = existante ? Secret.fromBase32(existante) : new Secret({ size: 20 });
  if (delegateId && !existante) {
    try {
      localStorage.setItem(PREFIXE + delegateId, JSON.stringify({ secret: secret.base32, exp: Date.now() + DUREE_MS }));
    } catch { /* navigation privée : on garde le comportement d'avant */ }
  }
  const totp = new TOTP({ issuer: "Aramis Enio", label: email || "Aramis", algorithm: "SHA1", digits: 6, period: 30, secret });
  return { secret: secret.base32, uri: totp.toString(), reprise: !!existante };
}

/** « ABCD EFGH … » : plus facile à relire et à recopier. */
export function formaterCle(base32) {
  return String(base32 || "").replace(/(.{4})/g, "$1 ").trim();
}

/** Appareil tactile (téléphone, tablette) : le QR de l'écran n'y est pas scannable. */
export function estAppareilTactile() {
  try { return window.matchMedia("(pointer: coarse)").matches; } catch { return false; }
}
